import { Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';

import { CalsiumListConfig } from '../../../calsiumlist/calsiumlist.component.model';

import { ProductionPriceList, CommonStateService, MasterDataStateService, ComponentType } from '../../../../state';
import { FormGroup, UntypedFormControl, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { ManualAdjustmentApiService } from 'src/api/services/manual-adjustment-api.service';
import { ManualAdjustment, ManualAdjustmentError, ManualAdjustmentValid } from '../../manual-adjustment-model';
import { ErrorHandlerService } from 'src/app/errorhandler/error-handler.service';
import { ErrorTypeEnum } from 'src/app/shared/enums.model';
import { ConfirmationDialogService } from 'src/app/common/confirmation-dialog/confirmation-dialog.service';

@Component({
    selector: 'cal-production-price-list',
    templateUrl: './pva-manual-adjustment.component.html',
    styleUrls: ['./pva-manual-adjustment.component.css'],
    standalone: false
})
export class PvaManualAdjustmentComponent implements OnInit {

    resources: any = this.commonService.getResources();
    componentTypes: Observable<ComponentType[]>;
    selectedPriceList: Observable<ProductionPriceList>;
    productionPriceListConfig: CalsiumListConfig;
    isPriceListsLoading: boolean = true;
    isResultsLoading: boolean = false;
    showComponentTypeTable: boolean = false;
    csvInput = new UntypedFormControl('', [Validators.required, this.csvFormatValidator(/\d+,"?.+"?,-?\d+,"?(MV|PV|AVC|CREWCAB)"?,"?.+"?/)]);
    validList: ManualAdjustmentValid[] = [];
    errorList: ManualAdjustmentError[] = [];
    manualAdjustmentList: ManualAdjustment[] = [];
    showConfirm = false;
    isLoading = false;
    constructor(private commonService: CommonStateService,
        private masterDataService: MasterDataStateService,
        private manualAdjustmentApiService: ManualAdjustmentApiService,
        private errHandler: ErrorHandlerService,
        private confirmationDialog: ConfirmationDialogService) { }

    ngOnInit() {
        this.masterDataService.loadComponentType();
        this.componentTypes = this.masterDataService.getComponentTypes();
    }

    async validate() {
        this.resetManualAdjustmentList();
        this.resetValidationResults();
        if (!this.csvInput.valid) return;
        this.csvInput.value.split("\n").forEach(line => {
            if (line.trim().length > 0) {
                const lineArr = line.split(',')
                const manualAdjustment: ManualAdjustment = {
                    componentTypeId: +lineArr[0],
                    productIndividualNumber: lineArr[1],
                    adjustment: +lineArr[2],
                    adjustmentType: lineArr[3],
                    comment: lineArr[4]
                }
                this.manualAdjustmentList.push(manualAdjustment);
            }
        });
        this.isLoading = true;
        const result = await this.manualAdjustmentApiService.verify(this.manualAdjustmentList);
        this.isLoading = false;
        if (result.errors) {
            result.errors.forEach((error) => {
                const message = error.message.replace('GraphQL.ExecutionError: ', '');
                this.errHandler.emitError(message, 'invalid format', ErrorTypeEnum.Error);
            });
        } else {
            this.validList = this.mapComponentTypeNames(result.data.manualAdjustment.verify.validList || []);
            this.errorList = this.mapComponentTypeNames(result.data.manualAdjustment.verify.errorList || []);
            this.showConfirm = true;
        }
    }

    async confirm() {
        if (this.validList.length === 0) {
            this.errHandler.emitError('No valid manual adjustments to submit.', 'error', ErrorTypeEnum.Error);
            return;
        }

        const result = await this.confirmationDialog.show(
            'Only valid List will be submitted. Are you sure you want to continue ?',
            this.resources.Confirm);
        if (!result) {
            return;
        }

        this.isLoading = true;
        this.showConfirm = false;
        const validManualAdjustments = this.validList.map(item => {
            return {
                componentTypeId: item.componentTypeId,
                productIndividualNumber: item.productIndividualNumber,
                adjustment: item.adjustment,
                adjustmentType: item.adjustmentType,
                comment: item.comment
            };
        });
        const confirmResult = await this.manualAdjustmentApiService.confirm(validManualAdjustments);
        this.isLoading = false;
        this.resetManualAdjustmentList();
        this.resetValidationResults();
        if (confirmResult.errors) {
            confirmResult.errors.forEach((error) => {
                const message = error.message.replace('GraphQL.ExecutionError: ', '');
                this.errHandler.emitError(message, 'invalid format', ErrorTypeEnum.Error);
            });
        } else if (confirmResult.data.manualAdjustment.confirm) {
            this.errHandler.emitError(this.resources.AdjustmentsSavedSuccessfully, this.resources.Success, ErrorTypeEnum.Success);
            this.csvInput.reset();
        } else {
            this.errHandler.emitError('error', 'error', ErrorTypeEnum.Error);
        }
    }

    back() {
        this.resetManualAdjustmentList();
        this.resetValidationResults();
        this.isLoading = false;
        this.showConfirm = false;
    }

    resetManualAdjustmentList() {
        this.manualAdjustmentList = [];
    }

    resetValidationResults() {
        this.validList = [];
        this.errorList = [];
    }

    mapComponentTypeNames<T extends ManualAdjustment>(items: T[]): Array<T & { componentTypeName: string }> {
        const componentTypes = this.masterDataService.getState().componentTypes || [];

        return items.map(item => ({
            ...item,
            componentTypeName: componentTypes.find(type => type.id === item.componentTypeId)?.name || item.componentTypeId.toString()
        }));
    }

    getErrorMessage() {
        const duplicateError = this.csvInput.getError('duplicateEntry');

        return this.csvInput.hasError('required') ? this.resources.RequiredFieldError :
            duplicateError ? `Duplicate entry found for Product Individual Number: ${duplicateError.productIndividualNumbers.join(', ')}` :
            this.csvInput.hasError('invalidFormat') ? this.resources.InvalidCSV :
            this.csvInput.hasError('exceededMaxEntries') ? "You can only enter a maximum of 500 entries." : '';
    }

    csvFormatValidator(pattern: RegExp): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const value: string = control.value;
            let valid = true;

            if (this.getLineCount(value) > 500) {
                return { 'exceededMaxEntries': { value: control.value } };
            }
            if (value !== null) {
                const lines = value.split(/\r?\n/)
                    .map(line => line.trim())
                    .filter(line => line.length > 0);
                const seenLines = new Set<string>();
                const duplicateProductIndividualNumbers = new Set<string>();

                lines.forEach(line => {
                    const fields = line.split(',');
                    const normalizedLine = fields.slice(0, 4)
                        .map(value => value.trim().toUpperCase())
                        .join(',');
                    const productIndividualNumber = fields[1]?.trim();

                    if (seenLines.has(normalizedLine) && productIndividualNumber) {
                        duplicateProductIndividualNumbers.add(productIndividualNumber);
                    }

                    seenLines.add(normalizedLine);
                });

                if (duplicateProductIndividualNumbers.size > 0) {
                    return {
                        'duplicateEntry': {
                            productIndividualNumbers: Array.from(duplicateProductIndividualNumbers)
                        }
                    };
                }
                lines.forEach(line => valid = valid && pattern.test(line.toUpperCase()));
            }
            return valid ? null : { 'invalidFormat': { value: control.value } };
        };
    }
    csvInputChange(value) {
        const newValue = value.split('\t').join(',');
            if (this.getLineCount(newValue) <= 500) {
                this.csvInput.setValue(newValue, { emitViewToModelChange: false });
            } else {
                this.errHandler.emitError("Maximum of 500 entries allowed.", "Limit Crossed", ErrorTypeEnum.Error);
                this.csvInput.setErrors({ 'exceededMaxEntries': true });
            }
    }

    getLineCount(value: string): number {
        return (value.match(/\n/g) || []).length + 1;
    }
}
